import { Controller } from "stimulus";
import { DirectUpload } from "@rails/activestorage";
import {
  getMetaValue,
  toArray,
  findElement,
  removeElement,
  insertAfter,
  sendDataToParent,
  emitFileUploadStarted, 
  emitOpenWidget,
  emitFileUploadFailed, 
} from "helpers";

// Import Dropzone and immediately disable auto-discovery
import Dropzone from "dropzone";
// Import Dropzone CSS directly alongside the JS
import "dropzone/dist/dropzone.css";
import "dropzone/dist/basic.css";
Dropzone.autoDiscover = false;

import {
  smallPreviewTemplate,
  defaultPreviewTemplate
} from "dropzone_templates";

export default class extends Controller {
  static targets = ["input"];

  connect() {
    this.dropZone = createDropZone(this);
    this.hideFileInput();
    this.bindEvents();
    Dropzone.autoDiscover = false;
    this.createPreviewTemplate();
  }

  get addButton() {
    // TODO: Would love a way to do this with targets
    return document.getElementById('add_files_button')
  }

  createPreviewTemplate() {
    // If there is an existing value (not none) in the form tag and preview = true and 
    // widget is not rendering in a modal 
    if ((this.elementValue != 'none') && (this.preview == "true") && (this.multiple === "false")) {
      //Remove default view
      document.querySelector(".dropzone-msg").style.display = "none";
      //Create preview element (can't reuse DZ template b/c image is not properly sized)
      var previewDiv = document.createElement("div");
      previewDiv.id = "small-widget"
      previewDiv.classList.add("image-form-value")
      previewDiv.classList.add("dz-preview")
      const preview = `
          <div class="dz-image">
            <img data-dz-thumbnail src=${this.elementValue} />
          </div>`;
      previewDiv.innerHTML = preview;
      this.dropZone.previewsContainer.appendChild(previewDiv);
    }
  };

  hideFileInput() {
    this.inputTarget.disabled = true;
    this.inputTarget.style.display = "none";
  }

  bindEvents() {
    this.dropZone.on("addedfile", file => {
      emitFileUploadStarted(this.dropzoneId);
      
      // Open widget modal if multiple files selected & disable add button 
      if (this.multiple === "true") {
        this.addButton.disabled = true
        // emitOpenWidget(this.dropzoneId);
      }
      // Remove manually created preview for existing value
      if (document.querySelector(".image-form-value") != null) {
        document.querySelector(".image-form-value").style.display = "none";
      }
      // Remove old file from file array and set status to canceled (file was already uploaded)
      // This is a workaround to allow for continuous file drops 
      if ((this.dropZone.files.length > 1) && (this.multiple === "false")) {
        this.dropZone.files[0].status = Dropzone.CANCELED;
        this.dropZone.removeFile(this.dropZone.files[0]); 
      }
      // Set the unique file uuid on the preview element to track cdn_url
      let uuid = file.upload.uuid 
      file.previewElement.setAttribute('data-uuid', uuid)

      setTimeout(()=> {
        // If file has been accepted and not manually canceled above - process it 
        if (file.accepted && file.status != "canceled") {
          // If there resize parameters, file is an image, and maxFiles = 1
          if ((this.dropZone.options.resizeWidth || this.dropZone.options.resizeHeight) && file.type.match(/image.*/) && !file.type.match(/image\/svg\+xml/) && this.multiple === "false") {
            // _transfromFiles[file] returns an array of transformedFile ln 1472 DZ source
            // Called at ln 1212 DZ source 
            this.dropZone._transformFiles([file], (transformedFile) => {
              transformedFile[0].name = file.name
              transformedFile[0].controller = file.controller
              transformedFile[0].previewElement = file.previewElement
              transformedFile[0].previewTemplate = file.previewTemplate
              createDirectUploadController(this, transformedFile[0]).start();
            })
          } else {
            createDirectUploadController(this, file).start();
          }
        }
      }, 500);
    });

    this.dropZone.on("removedfile", file => {
      file.controller && removeElement(file.controller.hiddenInput);
    })

    this.dropZone.on("error", (file, message) => {
      emitFileUploadFailed(this.dropzoneId, file, message);
      file.controller && removeElement(file.controller.hiddenInput);
      setTimeout(() => {
        this.dropZone.removeFile(file)

        //If .image-form-value has been previously created, show on error
        if (document.querySelector(".image-form-value") != null) {
          document.querySelector(".image-form-value").style.display = "block";
        }
      }, 4000);
    })

    this.dropZone.on("queuecomplete", file => {
      if (this.multiple === "true") this.addButton.disabled = false
    })

    this.dropZone.on("canceled", file => {
      file.controller && file.controller.xhr.abort();
    });

    //Source: https://github.com/enyo/dropzone/blob/10b65490a41232a76cc70ef40bb1b0a826a99eaf/dist/dropzone.js#L828
    //https://github.com/enyo/dropzone/blob/10b65490a41232a76cc70ef40bb1b0a826a99eaf/dist/dropzone.js#L740
    // TODO: Can set disable previews and remove this conditional?
    if (this.preview == "false" && this.multiple === "false") {
      this.dropZone.on("complete", file => {
        setTimeout(() => {
          if (file.previewElement != null && file.previewElement.parentNode != null) {
              file.previewElement.parentNode.removeChild(file.previewElement);
            }
          this.element.classList.remove("dz-started");
        }, 3000);
      });
    }
  }

  get small() {
    return this.data.get("small");
  }

  get headers() {
    return { "X-CSRF-Token": getMetaValue("csrf-token") };
  }

  get url() {
    return this.inputTarget.getAttribute("data-direct-upload-url");
  }

  get maxFiles() {
    return parseInt(this.data.get("maxFiles"));
  }

  get multiple() {
    return this.data.get("multiple").toString();
  }

  get bucketUuid(){
    return this.data.get("bucketUuid");
  }

  get dropzoneId(){
    return this.data.get("widgetId");
  }

  get maxFileSize() {
    return parseFloat(this.data.get("maxFileSize")) || 5;
  }

  get acceptedFiles() {
    if (this.data.get("acceptedFiles") == 'undefined') {
      return null;
    } else {
      return this.data.get("acceptedFiles");
    }
  }

  get addRemoveLinks() {
    if (this.data.get("removeLinks") == 'undefined') {
      return false;
    } else {
      return this.data.get("removeLinks");
    }
  }

  get elementValue() {
    return this.data.get("elementValue");
  }

  get preview() {
    return this.data.get("preview");
  }

  get tag() {
    if (this.data.get("tag") == 'undefined') {
      return null;
    } else {
      return this.data.get("tag");
    }
  }

  get resizeWidth() {
    if (this.data.get("resizeWidth") == 'undefined') {
      return null;
    } else {
      return this.data.get("resizeWidth");
    }
  }

  get resizeHeight() {
    if (this.data.get("resizeHeight") == 'undefined') {
      return null;
    } else {
      return this.data.get("resizeHeight");
    }
  }

  get resizeMethod() {
    if ((this.data.get("resizeMethod") == 'contain') || (this.data.get("resizeMethod") == 'crop')) {
      return this.data.get("resizeMethod");
    } else {
      return null;
    }
  }
}

class DirectUploadController {
  constructor(source, file) {
    this.directUpload = createDirectUpload(file, source.url, this);
    this.source = source;
    this.file = file;
  }

  start() {
    // The direct upload controller. Called for every individual file.
    this.file.controller = this; 
    this.hiddenInput = this.createHiddenInput();
    this.directUpload.create((error, attributes) => {
      if (error) {
        removeElement(this.hiddenInput);
        this.emitDropzoneError(error);
      } else {
        this.hiddenInput.value = attributes.signed_id;
        this.emitDropzoneSuccess();
      }
    });
  }

  createHiddenInput() {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = this.source.inputTarget.name;
    insertAfter(input, this.source.inputTarget);
    return input;
  }

  directUploadWillStoreFileWithXHR(xhr) {
    this.bindProgressEvent(xhr);
    this.emitDropzoneUploading();
  }

  bindProgressEvent(xhr) {
    this.xhr = xhr;
    this.xhr.upload.addEventListener("progress", event =>
      this.uploadRequestDidProgress(event)
    );
  }

  uploadRequestDidProgress(event) {
    const element = this.source.element;
    const progress = (event.loaded / event.total) * 100;
    findElement(
      this.file.previewTemplate,
      ".dz-upload"
    ).style.width = `${progress}%`;
  }

  emitDropzoneUploading() {
    this.file.status = Dropzone.UPLOADING;
    this.source.dropZone.emit("processing", this.file);
  }

  emitDropzoneError(error) {
    this.file.status = Dropzone.ERROR;
    this.source.dropZone.emit("error", this.file, error);
    this.source.dropZone.emit("complete", this.file);
  }

  emitDropzoneSuccess() {
    this.file.status = Dropzone.SUCCESS;
    this.source.dropZone.emit("success", this.file);
    this.source.dropZone.emit("complete", this.file);
    this.createNewImage(this.file, this.source.multiple)   
  }

  createNewImage(file, multiple) {
    let token = document.querySelector("meta[name='csrf-token']").content;
    let bucket = this.source.bucketUuid
    let tag = this.source.tag
    let url = `/api/v1/buckets/${bucket}/images/batch_upload.json?tag=${tag}`
    let signed_id = this.hiddenInput.value
    fetch(url,
    {
      headers: {
        'accept': 'application/json',
        'content-type': 'application/json',
        'X-CSRF-Token': token
      },
      method: 'post',
      body: JSON.stringify({ signed_id }),

    })
    .then(response => response.json())
    .then(data => {
     // The cdn_url needs to be put on the file element in the dropzone files array
     // The blob file and dropzone files share a preview element, so pull the uuid from the preview element, iterate through the dropzone array to find
     // the correct file, and add a cdn_url value 
      let current_uuid = file.previewElement.dataset.uuid 
      let dz_file = file.controller.source.dropZone.files.find(element => element.upload.uuid === current_uuid) 
      dz_file.cdnUrl = data.data.attributes["cdn-url"]

      // Immediately emit and handle event if single file selected
      if (multiple === "false") {
        const fileDetails = [{
          cdnUrl: dz_file.cdnUrl, 
          name: dz_file.name, 
          size: dz_file.size, 
          type: dz_file.type, 
          aiUrl: data.data.attributes["ai-url"],
          id: parseInt(data.data.id, 10)
        }]
        sendDataToParent(fileDetails, this.source.dropzoneId, data.data.attributes["cdn-url"], false)
      }
    })
  }
}

function createDirectUploadController(source, file) {
  return new DirectUploadController(source, file);
}

function  createDirectUpload(file, url, controller) {
  return new DirectUpload(file, url, controller);
}

function createDropZone(controller) {
  let preview = defaultPreviewTemplate;

  if (controller.small == "true") {
    preview = smallPreviewTemplate;
  }

  return new Dropzone(controller.element, {
    url: controller.url,
    headers: controller.headers,
    maxFiles: controller.maxFiles,
    maxFilesize: controller.maxFileSize,
    acceptedFiles: controller.acceptedFiles,
    addRemoveLinks: controller.addRemoveLinks,
    resizeWidth: controller.resizeWidth,
    resizeHeight: controller.resizeHeight,
    resizeMethod: controller.resizeMethod,
    autoQueue: false,
    previewTemplate: preview,
  });
}